import React, { useCallback, useContext, useEffect } from "react";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import { buildLink, getPath } from "common/routing";
import { SearchResultProps } from "common-client/utils/search";
import { useGetOrCreateGuestPropertyLazyQuery } from "../../generated/graphql";
import { track } from "../../utils/tracking";
import { getCurrentURL } from "../../utils/url";
import { AuthContext } from "../Authorization/AuthContext";
import Icon, { ICON_COLORS, Icons } from "../Common/Icons";
import { LayeredMap } from "../Maps/LayeredMap";
import { LayerContextProvider } from "../Maps/layers";
import {
  MapViewportContext,
  MapViewportContextProvider,
} from "../Maps/utils/MapViewportContext";
import { Bounds } from "../Maps/utils/viewportHook";
import { OldOrNewSearch } from "../Search";
import { SearchContext, SearchContextProvider } from "../Search/SearchContext";
import {
  Explore,
  FooterWrapper,
  Loading,
  SearchBox,
  Title,
} from "./__styles__/Guest";
import { Footer } from "./Layout";

const ExploreButton = () => {
  const { t } = useTranslation();
  const { searchQueryState } = useContext(SearchContext);

  if (searchQueryState.searchPanelOpen) {
    return null;
  }

  return (
    <Explore
      onClick={() => {
        track("Map Expanded", {
          source: "search",
        });
      }}
      to={{
        pathname: getPath("explore"),
      }}
    >
      {t("button-explore-map")}
    </Explore>
  );
};

const InnerGuestLandingPageMap = ({
  hasFeaturedContent,
}: {
  hasFeaturedContent: boolean;
}) => {
  const history = useHistory();
  const location = useLocation();
  const account = useContext(AuthContext).account!;
  const [
    getOrCreateProperty,
    { loading: propertyLoading, data: propertyData },
  ] = useGetOrCreateGuestPropertyLazyQuery();
  const { zoomTo } = useContext(MapViewportContext);

  const prevLocation = getCurrentURL(location);

  useEffect(() => {
    if (propertyData) {
      const pathname =
        propertyData.getProperty?.id ?
          buildLink("residentProperty", {
            accountId: account.id,
            propertyId: propertyData.getProperty.id,
          })
        : buildLink("noProperty", {
            accountId: account.id,
          });

      history.push({
        pathname,
        state: {
          prevLocation,
        },
      });
    }
  }, [propertyData, account.id, history, prevLocation]);

  const handleSearchResult = useCallback(
    (searchResultData: SearchResultProps) => {
      track("Result clicked");

      void getOrCreateProperty({
        variables: {
          address: searchResultData.address,
          longitude: searchResultData.point.longitude,
          latitude: searchResultData.point.latitude,
          id: searchResultData.propertyId,
          parcelId: searchResultData.geocacheParcelId,
          improveParcelCoordinates: !searchResultData.propertyId,
        },
      });
    },
    [getOrCreateProperty],
  );

  /**
   * Ensures the map is resized correctly when the window is resized via zoom or
   * viewport change.
   */
  useEffect(() => {
    const handleResize = debounce(() => {
      zoomTo({ bounds: account.bounds as Bounds });
    }, 500);

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      handleResize.cancel();
    };
  }, [account.bounds, zoomTo]);

  const layers = {
    accountBoundary: {},
    parcels: {},
    firms: { firms: [] },
  };

  if (propertyLoading) {
    return (
      <Loading>
        <Icon icon={Icons.LOADING} color={ICON_COLORS.LIGHT_GREY} />
      </Loading>
    );
  }

  const title = account.publicPortal.homepageTitle;

  return (
    <LayerContextProvider account={account!}>
      <SearchContextProvider
        handleSearchResultClick={handleSearchResult}
        enableRecentSearches
      >
        <LayeredMap
          width="100%"
          height="100%"
          baseMapStyle={account.defaultBaseMap.mapboxStyle}
          account={account}
          layers={layers}
          shader={{ hasFeaturedContent }}
          centerMapOverlayItems={[
            <Title>{title}</Title>,
            <SearchBox>
              <OldOrNewSearch
                compactSearchPanel
                overlaySearchPanel={hasFeaturedContent}
              />
            </SearchBox>,
            <ExploreButton />,
            !hasFeaturedContent ?
              <FooterWrapper>
                <Footer map />
              </FooterWrapper>
            : null,
          ].filter(Boolean)}
        />
      </SearchContextProvider>
    </LayerContextProvider>
  );
};

export const GuestLandingPageMap = ({
  hasFeaturedContent,
}: {
  hasFeaturedContent: boolean;
}) => {
  const { account } = useContext(AuthContext);
  if (!account) {
    return null;
  }

  return (
    <MapViewportContextProvider
      initialViewport={{ bounds: account.bounds as Bounds }}
    >
      <InnerGuestLandingPageMap hasFeaturedContent={hasFeaturedContent} />
    </MapViewportContextProvider>
  );
};
